/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { getFormValue, removeFormValue, setFormValue, updateForm } from '../..';
import { isNullOrUndefined } from '../../../react/components/form';
import { parseFieldIdentifier } from '../../../react/components/form/services/processor';
import { has } from 'json-pointer';
import moment from 'moment';
import { combineEpics } from 'redux-observable';
import { BehaviorSubject, from, merge, of } from 'rxjs';
import { ajax, AjaxError, AjaxResponse } from 'rxjs/ajax';
import { catchError, count, debounceTime, delay, filter, map, mapTo, pluck, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';
import { AppAction } from '../..';
import config from '../../../config';
import { createEpic, getCommonHeaders } from '../../../utils/epics';
import { allDataUploaded, beginPublish, cleanupActions, doPublishActions, fetchEvalPatchesActions, networkUpForPublish, patchQueued, publishPromptActions } from '../actions';
import { cancelPublish, signOutAction } from './../..';
import { Evaluation } from './../../../evaluation';
import { LAST_MODIFIED } from './../../../utils/types';
import { networkDownForPublish } from './../actions';
import { ExpressionManager } from '../../../react/components/form/services/ExpressionManager';

// This epic and the signalDoPublishEpic happen in parallel. They should happen in series.
const networkCheckEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(beginPublish)),
    pluck('payload'),
    switchMap((evalID) => {
      const evl = state$.value.evals.items.get(evalID)!;
      const type = evl.formType;
      const evalUri = `${config.dfdmHost}/api/evaluations/${type}/${evl.uuid!}`;

      return from(
        // Make sure publish point is reachable
        fetch(evalUri, {
          method: 'OPTIONS',
        })
      ).pipe(
        switchMap((res) => {
          if (res.status === 401) return of(signOutAction(), cancelPublish());
          else return of(networkUpForPublish(), cleanupActions.request(evl));
        }),
        catchError((err) => {
          // Network connection problem
          return of(networkDownForPublish());
        })
      );
    })
  );
});

const cleanupEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(cleanupActions.request)),
    pluck('payload'),
    switchMap((evl) => {
      // Connection available. Perform cleanup operation
      const changes: AppAction[] = [];
      const form = state$.value.forms.items[evl.formType][evl.formVersion]!;

      const expMgr = new ExpressionManager(new BehaviorSubject(evl.data));

      // Display any prompts the user has to acknowledge
      if (has(form, '/definition/hooks/publish/prompts')) {
        form.definition.hooks.publish!.prompts!.forEach((prompt) => {
          const message = prompt.text;
          if (prompt.applicable) {
            expMgr
              .watch(prompt.applicable)
              .subscribe((isApplicable) => {
                if (isApplicable) {
                  alert(message);
                }
              })
              .unsubscribe();
          } else {
            alert(message);
          }
        });
      }

      if (has(form, '/definition/hooks/publish/cleanup')) {
        Object.entries(form.definition.hooks.publish!.cleanup!).forEach(([dataPath, expr]) => {
          try {
            expMgr
              .watch(expr)
              .subscribe((val) => {

                // don't set duplicates here, it just creates unecessary patches.
                const currentValue = getFormValue(dataPath, evl.data, "");

                if (currentValue === val || isNullOrUndefined(currentValue) && isNullOrUndefined(val)) {
                  return;
                }

                if (val !== undefined && val !== null) {
                  changes.push(setFormValue(dataPath, val, evl.data, evl.id));
                } else if (has(evl.data, parseFieldIdentifier(dataPath))) {
                  changes.push(removeFormValue(dataPath, evl.id));
                }
              })
              .unsubscribe();
          } catch (error) {
            console.log('Error within hook', dataPath, expr, error);
            throw error;
          }
        });
      }

      expMgr.complete();

      changes.push(cleanupActions.success(), publishPromptActions.request(evl.uuid!));
      return from(changes).pipe(delay(1));
    })
  );
});
// try with the changes only to the cleanup? or only to the computed values?


// Publish prompts will be handled by EvalEditor, just wait for signal that things are ready
//
// const publishPromptsHook = createEpic((action$, state$) => {
//   return action$.pipe(
//     filter(isActionOf(beginPublish)),
//     pluck('payload'),
//     switchMap(evalID => action$.pipe(
//       filter(isActionOf(cleanupActions.success)),
//       switchMap(() => {
//         const { formType, formVersion } = state$.value.evals.items[evalID];
//         const form = state$.value.forms.items[formType][formVersion]!;
//         if (has(form, '/definition/hooks/publish/prompts'))
//         form.definition
//       })
//     ))
//   )
// })

/**
 * First step in publish flow
 * 
 * This step sometimes ends prematurely after computed values are triggered in the cleanup
 * Solution can be to not trigger computed values during cleanup, or debounce the takeuntil here 
 */
const signalDoPublishEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(beginPublish)),
    pluck('payload'),
    switchMap((evalID) =>
      action$.pipe(
        // needs to also accept computed values changes
        filter(isActionOf(updateForm)),
        takeUntil(
          // The prompt completion is what we key off of
          action$.pipe(
            filter(isActionOf(publishPromptActions.success))
          )
        ),
        count(),
        switchMap((updates) => {
          const { formType, uuid } = state$.value.evals.items.get(evalID)!;
          const completionAction = doPublishActions.request({
            uuid: uuid!,
            evalID,
            formType,
          });

          if (updates) {
            return merge(
              action$.pipe(
                filter(isActionOf(patchQueued)), // wait for all-clear AFTER patches queued
                skip(updates - 1),
                // last update queued, NOW look for all-clear
                switchMap(() => action$.pipe(filter(isActionOf(allDataUploaded)))),
                filter((action) => action.payload),
                // okay to move on now that all data is uploaded
                mapTo(completionAction),
                take(1)
              ),
              of(fetchEvalPatchesActions.request(uuid!)) // kick off a sync
            );
          } else {
            return of(completionAction);
          }
        })
      )
    )
  );
});

/**
 * Should only be triggered when all patches have been sent to the server.
 */
const doPublishEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(doPublishActions.request)),
    pluck('payload'),
    switchMap(({ uuid, formType, evalID }) => {
      const evl = state$.value.evals.items.get(evalID)!;
      return ajax
        .post(
          `${config.dfdmHost}/api/evaluations/${formType}/${uuid}`,
          undefined, // no payload
          getCommonHeaders(state$)
        )
        .pipe(
          map((res: AjaxResponse) => {
            const evl: Evaluation = res.response;
            const lastSync = moment(res.xhr.getResponseHeader(LAST_MODIFIED)!).toISOString();
            const dbPublished = true;
            const now = moment().toISOString();
            const firstPublished = evl.firstPublished ?? now;
            return doPublishActions.success({
              ...evl,
              uuid: evl.id,
              id: evalID,
              lastSync,
              dbPublished,
              firstPublished
            });
          }),
          catchError((e: AjaxError) => {
            if (e.status === 401) return of(signOutAction(), cancelPublish());
            else return of(doPublishActions.failure(e));
          })
        );
    })
  );
});

export default combineEpics(networkCheckEpic, cleanupEpic, signalDoPublishEpic, doPublishEpic);
